
import { defineComponent } from "vue";
import {
  GammaMapLegendDisplayMode,
  GammaMapLegendItemType,
  IGammaMapLegendItem,
  IMapLayerLegend,
  LayerControl,
  MapLayerGroup,
} from "@/types";
import { mapState } from "pinia";
import { useMap, useOrgSettings } from "@/services";

export default defineComponent({
  name: "MapLayerLegend",
  props: {
    layerControl: {
      type: LayerControl,
      required: true,
    },
  },
  computed: {
    ...mapState(useOrgSettings, ["layerSettings"]),
    ...mapState(useMap, ["loadedBooks"]),
    layerGroup(): MapLayerGroup {
      return this.layerControl.activeGroup || ({} as MapLayerGroup);
    },
    groupName(): string {
      return this.layerGroup?.name || "";
    },
    legend(): IMapLayerLegend {
      // review if defined in settings
      if (this.layerGroup.legend?.legendItems) {
        return this.layerGroup.legend;
      }
      // load from layer paint properties
      let legend = {} as IMapLayerLegend;
      const layerSettings =
        this.layerSettings.find(
          (l: any) => l.id === this.layerControl.layerIds[0]
        ) ?? this.loadedBooks.get(this.layerControl.layerIds[0]);
      if (layerSettings) {
        switch (layerSettings.type) {
          case "line":
          case "fill":
          case "circle": {
            const colorProperty = layerSettings.type + "-color";
            if (Array.isArray(layerSettings.paint[colorProperty])) {
              const colors = layerSettings.paint[colorProperty].filter(
                (entry: any) =>
                  !Array.isArray(entry) &&
                  entry !== "case" &&
                  entry !== "#c0c0c0"
              );
              const keys: any[] = [];
              const legendItems: IGammaMapLegendItem[] = [];
              const isGraduated =
                layerSettings.paint[colorProperty]
                  .flat()
                  .filter(
                    (entry: any) =>
                      !Array.isArray(entry) &&
                      typeof entry === "string" &&
                      entry === "=="
                  ).length === 0;
              layerSettings.paint[colorProperty]
                .filter((p: any) => Array.isArray(p))
                .forEach((element: any) => {
                  keys.push(
                    element.filter(
                      (entry: any) =>
                        !Array.isArray(entry) &&
                        (typeof entry === "number" ||
                          (typeof entry === "string" &&
                            !["case", "==", ">"].includes(entry)))
                    )[0]
                  );
                });
              for (let i = 0; i < colors.length; i++) {
                const bottomRange = keys[i];
                const topRange = keys[i + 1];
                legendItems.push({
                  description: isGraduated
                    ? i !== colors.length - 1
                      ? bottomRange + " - " + topRange
                      : " < " + bottomRange
                    : bottomRange,
                  color: colors[i],
                });
              }
              legend = {
                legendItems: legendItems,
                min:
                  this.layerControl.layerIds.length === 1 &&
                  this.layerGroup.legend?.min === undefined
                    ? keys[keys.length - 1]
                    : this.layerGroup.legend?.min || "n/a",
                max:
                  this.layerControl.layerIds.length === 1 &&
                  this.layerGroup.legend?.max === undefined
                    ? keys[0]
                    : this.layerGroup.legend?.max || "n/a",
                display:
                  this.layerGroup.legend?.display ||
                  GammaMapLegendDisplayMode.Vertical,
                type: GammaMapLegendItemType[
                  (layerSettings.type.charAt(0).toUpperCase() +
                    layerSettings.type.slice(
                      1
                    )) as keyof typeof GammaMapLegendItemType
                ],
              };
            } else {
              legend = {
                legendItems: [
                  {
                    description: this.groupName,
                    color: layerSettings.paint[colorProperty],
                  },
                ],
                display: GammaMapLegendDisplayMode.Horizontal,
                type: GammaMapLegendItemType[
                  (layerSettings.type.charAt(0).toUpperCase() +
                    layerSettings.type.slice(
                      1
                    )) as keyof typeof GammaMapLegendItemType
                ],
              };
            }
            break;
          }
          case "raster": {
            console.warn(
              "Raster layers styles must be set in organisations settings"
            );
            legend = {
              legendItems: [],
              display:
                this.layerGroup.legend?.display ||
                GammaMapLegendDisplayMode.Vertical,
              type: GammaMapLegendItemType.Fill,
            };
            break;
          }
          default: {
            console.warn(
              "legend() - Not implemented for " + layerSettings.type
            );
            legend = {
              legendItems: [],
              display:
                this.layerGroup.legend?.display ||
                GammaMapLegendDisplayMode.Vertical,
              type: GammaMapLegendItemType.Fill,
            };
            break;
          }
        }
      }
      return legend;
    },
  },
});
